/* eslint-disable react/prop-types */
import React from "react";
import { Button, makeStyles, Tooltip } from "@material-ui/core";
import { RiFileExcel2Line } from "react-icons/ri";
import getAttach from "lib/requests/attach";
import mixpanelTrack from "app/common/MixpanelComponent";
import _ from "lodash";
import JSZip from "jszip";

const useStyles = makeStyles(() => ({
  excelButton: {
    position: "absolute",
    width: "50px",
    height: "50px",
    right: "0",
    top: "0",
    "&.MuiButton-root": {
      "&:hover": {
        backgroundColor: "#fafafa",
      },
    },
  },
}));

export default function CompetitorExportExceButton({
  competitorScenarioData,
  competitorSliderData,
}) {
  const classes = useStyles();

  const getCompetitorsFiles = async (competitorScenario) => {
    const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunk size
    const headers = {};
    const files = [];

    const pathToFiles = [
      competitorScenario.pathS3AffectedScenario,
      competitorScenario.pathS3MarketShare,
    ];
    for (let i = 0; i < pathToFiles.length; i += 1) {
      const path = pathToFiles[i];
      let fullFile = new Blob();

      headers.Range = `bytes=0-${CHUNK_SIZE - 1}`;
      // Request the total file size to determine the number of requests to be made
      // eslint-disable-next-line no-await-in-loop
      const response = await getAttach(
        {
          path,
          file: "csv",
        },
        headers
      );
      fullFile = new Blob([fullFile, response.data]);
      const fileSize = parseInt(
        response.headers["content-range"].split("/")[1],
        10
      );
      const totalChunks = Math.ceil(fileSize / CHUNK_SIZE);

      // Iterate over the number of chunks and fetch each chunk with Range header
      // Create an array of Promises for fetching chunks
      const chunkPromises = [];
      for (let x = 1; x < totalChunks; x += 1) {
        const startByte = x * CHUNK_SIZE;
        const endByte = startByte + CHUNK_SIZE - 1;
        headers.Range = `bytes=${startByte}-${endByte}`;
        chunkPromises.push(getAttach({ path, file: "csv" }, headers));
      }
      // Wait for all chunk requests to complete
      // eslint-disable-next-line no-await-in-loop
      const chunkResponses = await Promise.all(chunkPromises);
      _.forEach(chunkResponses, (chunkResponse) => {
        fullFile = new Blob([fullFile, chunkResponse.data]);
      });

      files.push({
        fileName: `${competitorScenario.cycleLabel}-${
          competitorScenario.name
        }-${i === 0 ? "Affected Scenario" : "Market Share"}.csv`,
        file: fullFile,
      });
    }

    return files;
  };

  const handleExportExcelClick = async () => {
    /* MIXPANEL TRACKS */
    mixpanelTrack(`Export competitors`);
    /* END OF MIXPANEL TRACKS */
    const competitorSliderDataWithProductLabelSpecificKeys = [];
    _.forEach(competitorSliderData, (category) => {
      _.forEach(category.companies, (company) => {
        competitorSliderDataWithProductLabelSpecificKeys.push({
          "category label": category.label,
          "company label": company.label,
          "price percentage": Math.abs(company.sliderValue),
          "price direction": company.sliderValue < 0 ? "decrease" : "increase",
        });
      });
    });
    // Function to convert an object to a CSV row
    function objectToCsvRow(obj) {
      const values = Object.values(obj);
      return values.map((value) => `"${value}"`).join(",");
    }

    // Convert the array of objects to a CSV string
    function arrayToCsv(data) {
      const csvHeader = Object.keys(data[0]).join(",");
      const csvRows = data.map(objectToCsvRow);
      return [csvHeader, ...csvRows].join("\n");
    }

    const csvContent = arrayToCsv(
      competitorSliderDataWithProductLabelSpecificKeys
    );
    const csvBlob = new Blob([csvContent], { type: "text/csv" });
    const files = await getCompetitorsFiles(competitorScenarioData);
    files.push({ fileName: "prices-configuration.csv", file: csvBlob });
    const zip = new JSZip();
    // Add files to the ZIP archive
    files.forEach((fileObject) => {
      zip.file(fileObject.fileName, fileObject.file);
    });
    zip.generateAsync({ type: "blob" }).then((content) => {
      // Create a Blob with the ZIP content
      const blob = new Blob([content]);

      // Create a download link for the Blob
      const url = window.URL.createObjectURL(blob);

      // Create an anchor element for downloading
      const a = document.createElement("a");
      a.href = url;
      a.download = `${competitorScenarioData.cycleLabel}-${competitorScenarioData.name}-competitors.zip`;
      a.style.display = "none";

      // Append the anchor element to the document and trigger the download
      document.body.appendChild(a);
      a.click();

      // Clean up by removing the anchor element and revoking the Blob URL
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    });
  };

  return (
    <Tooltip title="Export to Excel" placement="left">
      <Button
        className={classes.excelButton}
        aria-label="export to excel"
        component="span"
        disableRipple
        onClick={() => handleExportExcelClick()}
      >
        <RiFileExcel2Line size={18} />
      </Button>
    </Tooltip>
  );
}
